import React from 'react';
import { useTranslations } from '@unisporkal/localization';
import { List } from '@unisporkal/alliance-ui';
import WarningBox from '../../../components/WarningBox/WarningBox';
import StaticAgreementRow from './StaticAgreementRow';
import PaAgreementOption from './PaAgreementOption';
import AgreementSummaryList from './AgreementSummaryList';
import { PREMIUM_ACCESS_TYPE, type BatchAgreementsProps } from './types';
import styles from './BatchAgreements.module.scss';

const BatchAgreements: React.FC<BatchAgreementsProps> = ({
  batchAgreements,
  availablePremiumAccessAgreements,
  userHasMultiplePaAgreements,
  showPaPicker,
  currentPaAgreementId,
  isSwitchingPremiumAccessAgreement,
  switchingToPremiumAccessId,
  switchPremiumAccessAgreementError,
  onSelectPaAgreement,
  isSingleAgreement,
  canEditAgreementSelection,
  multiPaBulkDownloadActive,
}) => {
  const t = useTranslations();

  const renderBatchAgreementOptions = () =>
    batchAgreements.flatMap((agreement) => {
      if (agreement.productType === PREMIUM_ACCESS_TYPE && showPaPicker) {
        return (availablePremiumAccessAgreements ?? []).map((paAgreement) => (
          <PaAgreementOption
            key={paAgreement.id}
            paAgreement={paAgreement}
            isSelected={paAgreement.id === currentPaAgreementId}
            isSwitchingToThis={
              isSwitchingPremiumAccessAgreement &&
              switchingToPremiumAccessId === String(paAgreement.id)
            }
            disabled={isSwitchingPremiumAccessAgreement}
            onSelect={() => onSelectPaAgreement(paAgreement.id)}
          />
        ));
      }
      return (
        <StaticAgreementRow
          key={agreement.id}
          agreement={agreement}
          userHasMultiplePaAgreements={userHasMultiplePaAgreements}
          showOverageIndicator={multiPaBulkDownloadActive}
        />
      );
    });

  if (batchAgreements.length === 0) {
    return <WarningBox heading={t('no_agreement_available')} />;
  }

  if (isSingleAgreement || !canEditAgreementSelection) {
    return (
      <AgreementSummaryList
        batchAgreements={batchAgreements}
        userHasMultiplePaAgreements={userHasMultiplePaAgreements}
        showOverageIndicator={multiPaBulkDownloadActive}
      />
    );
  }

  return (
    <section
      className={styles.container}
      data-testid="batch-agreements"
    >
      <h3 className={styles.heading}>{t('agreements')}</h3>
      <List
        className={styles.table}
        aria-busy={isSwitchingPremiumAccessAgreement}
      >
        {renderBatchAgreementOptions()}
      </List>
      {switchPremiumAccessAgreementError && (
        <p
          className={styles.switchError}
          data-testid="agreement-switch-error"
        >
          {t('agreement_switch_error')}
        </p>
      )}
    </section>
  );
};

export default BatchAgreements;
